import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import {
  ClipboardList,
  Eye,
  ArrowLeft,
  Camera,
  Upload,
  HelpCircle,
  ShieldAlert,
} from 'lucide-react';
import { ViewPosition } from '../types/types';
import { PunchItemHotspotForm } from './PunchItemHotspotForm';
import { QuickPunchItemHotspotForm } from './QuickPunchItemHotspotForm';
import { ObservationHotspotForm } from './ObservationHotspotForm';
import { RfiHotspotForm } from './RfiHotspotForm';
import { SafetyHotspotForm } from '@/features/safety/SafetyHotspotForm';
import {
  AttachmentPhotoGallery,
  type AttachmentPhoto,
} from '@/shared/components/AttachmentPhotoGallery';
import { uploadProjectPhotoInBackground } from './backgroundPhotoUpload';
import type { PhotoSourceType } from '@/features/photo-gallery/types';
import type { PendingPhotoUpload } from './pendingPhotoUpload';
import { ironview360ViewerExtensionRegistry } from '../extensions/ironview360Extensions';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { useFeatureAccess } from '@/shared/hooks/useFeatureAccess';

type ItemType = 'punch_item' | 'quick_punch_item' | 'observation' | 'rfi' | 'safety';
type DialogStep = 'type' | 'photos' | 'form';

const SOURCE_TYPE_BY_ITEM: Record<ItemType, PhotoSourceType> = {
  punch_item: 'punchlist',
  quick_punch_item: 'punchlist',
  observation: 'observation',
  rfi: 'other',
  safety: 'other',
};

interface FABActionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  photoId?: number;
  projectId?: string;
  viewPosition?: ViewPosition | null;
  onSuccess?: () => void;
  onHotspotCreated?: () => Promise<void>;
  hotspotId?: number;
}

const ITEM_TYPES = (
  ['quick_punch_item', 'punch_item', 'observation', 'rfi', 'safety'] as const
).map((key) => {
  const extension = ironview360ViewerExtensionRegistry.hotspotObjects.get(key);
  if (!extension) {
    throw new Error(`Missing Ironview 360 hotspot extension: ${key}`);
  }
  return {
    key,
    label: extension.label,
    description: extension.description ?? '',
    icon:
      extension.icon ??
      (key === 'observation'
        ? Eye
        : key === 'rfi'
          ? HelpCircle
          : key === 'safety'
            ? ShieldAlert
            : ClipboardList),
  };
});

const ITEM_TITLES: Record<ItemType, string> = {
  punch_item: 'New Punch Item',
  quick_punch_item: 'Configure Quick Punch Preferences',
  observation: 'New Observation',
  rfi: 'New RFI',
  safety: 'New Safety Issue',
};

const ITEM_DESCRIPTIONS: Record<ItemType, string> = {
  punch_item: 'Create a punch item pinned to this location in the 360° view.',
  quick_punch_item: '',
  observation: 'Create an observation pinned to this location in the 360° view.',
  rfi: 'Create an RFI pinned to this location in the 360° view.',
  safety: 'Create a safety issue pinned to this location in the 360° view.',
};

const FABActionDialog = ({
  open,
  setOpen,
  photoId,
  projectId,
  viewPosition,
  onSuccess,
  onHotspotCreated,
  hotspotId,
}: FABActionDialogProps) => {
  const [selectedType, setSelectedType] = useState<ItemType | null>(null);
  const [step, setStep] = useState<DialogStep>('type');
  const [selectedPhotos, setSelectedPhotos] = useState<PendingPhotoUpload[]>([]);
  const takePhotoInputRef = useRef<HTMLInputElement>(null);
  const quickPunchPhotoInputRef = useRef<HTMLInputElement>(null);
  const uploadPhotoInputRef = useRef<HTMLInputElement>(null);
  const numericProjectId = projectId ? Number(projectId) : undefined;
  const safetyAccess = useFeatureAccess(FEATURE_FLAGS.SAFETY);
  const availableItemTypes = useMemo(
    () => ITEM_TYPES.filter(({ key }) => key !== 'safety' || safetyAccess.isEnabled),
    [safetyAccess.isEnabled]
  );

  const clearSelectedPhotos = useCallback(() => {
    setSelectedPhotos((prev) => {
      prev.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      return [];
    });
  }, []);

  const handleClose = () => {
    setSelectedType(null);
    setStep('type');
    clearSelectedPhotos();
    setOpen(false);
  };

  const handleFormSuccess = async () => {
    await onHotspotCreated?.();
    onSuccess?.();
    setSelectedType(null);
    setStep('type');
    clearSelectedPhotos();
  };

  const appendPhotos = useCallback(
    (files: File[], itemType = selectedType) => {
      if (files.length === 0) return;
      if (!numericProjectId || !itemType) return;

      const sourceType = SOURCE_TYPE_BY_ITEM[itemType];

      const newEntries: PendingPhotoUpload[] = files.map((file, index) => {
        const id = `${file.name}-${file.lastModified}-${Date.now()}-${index}`;
        const previewUrl = URL.createObjectURL(file);

        const uploadPromise = uploadProjectPhotoInBackground({
          projectId: numericProjectId,
          file,
          sourceType,
        });

        // Update local state when the upload settles. Guard against the
        // entry having been removed in the meantime.
        uploadPromise
          .then((result) => {
            setSelectedPhotos((prev) =>
              prev.map((p) => (p.id === id ? { ...p, status: 'done', photoId: result.photoId } : p))
            );
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Upload failed.';
            setSelectedPhotos((prev) =>
              prev.map((p) => (p.id === id ? { ...p, status: 'failed', error: message } : p))
            );
          });

        return {
          id,
          file,
          previewUrl,
          status: 'uploading',
          uploadPromise,
        };
      });

      setSelectedPhotos((prev) => [...prev, ...newEntries]);
    },
    [numericProjectId, selectedType]
  );

  const removePhoto = (attachmentId: string) => {
    setSelectedPhotos((prev) => {
      const match = prev.find((photo) => photo.id === attachmentId);
      if (match) URL.revokeObjectURL(match.previewUrl);
      return prev.filter((photo) => photo.id !== attachmentId);
    });
  };

  const photoAttachments = useMemo<AttachmentPhoto[]>(
    () =>
      selectedPhotos.map((photo) => ({
        id: photo.id,
        file: photo.file.name,
        file_url: photo.previewUrl,
      })),
    [selectedPhotos]
  );

  const handleChooseType = (type: ItemType) => {
    setSelectedType(type);
    if (type === 'quick_punch_item') {
      clearSelectedPhotos();
      setStep('form');
      return;
    }
    setStep('photos');
  };

  const renderTypeSelection = () => (
    <div className="space-y-2 pt-1">
      {availableItemTypes.map(({ key, label, description, icon: Icon }) => (
        <button
          key={key}
          type="button"
          className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
          onClick={() => handleChooseType(key)}
        >
          <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Icon className="size-5 text-primary" />
          </div>
          <div>
            <div className="font-medium text-sm">{label}</div>
            <div className="text-xs text-muted-foreground">{description}</div>
          </div>
        </button>
      ))}
    </div>
  );

  const renderPhotoSelection = () => (
    <div className="space-y-4 pt-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          className="w-full rounded-lg border border-border p-4 text-left hover:bg-accent transition-colors"
          onClick={() => takePhotoInputRef.current?.click()}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Camera className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm font-medium">Take Photo</p>
              <p className="text-xs text-muted-foreground">
                Open camera and add one photo at a time.
              </p>
            </div>
          </div>
        </button>
        <button
          type="button"
          className="w-full rounded-lg border border-border p-4 text-left hover:bg-accent transition-colors"
          onClick={() => uploadPhotoInputRef.current?.click()}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm font-medium">Upload Photo</p>
              <p className="text-xs text-muted-foreground">
                Select one or more photos from your device.
              </p>
            </div>
          </div>
        </button>
      </div>

      <input
        ref={takePhotoInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          appendPhotos(Array.from(event.target.files ?? []));
          event.currentTarget.value = '';
        }}
      />
      <input
        ref={uploadPhotoInputRef}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={(event) => {
          appendPhotos(Array.from(event.target.files ?? []));
          event.currentTarget.value = '';
        }}
      />

      {photoAttachments.length > 0 && (
        <div className="space-y-2">
          <AttachmentPhotoGallery
            attachments={photoAttachments}
            maxVisible={6}
            onDelete={removePhoto}
          />
          <p className="text-xs text-muted-foreground">
            Added {photoAttachments.length} photo{photoAttachments.length === 1 ? '' : 's'}. You can
            keep adding more.
          </p>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={() => setStep('type')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => setStep('form')}>
            Skip
          </Button>
          <Button
            type="button"
            onClick={() => setStep('form')}
            disabled={photoAttachments.length === 0}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );

  const renderForm = () => {
    if (!photoId || !viewPosition) {
      return (
        <div className="text-center text-muted-foreground py-4">
          Please open a 360° photo first.
        </div>
      );
    }

    const formProps = {
      photoId,
      projectId: numericProjectId,
      viewPosition,
      hotspotId,
      onSuccess: handleFormSuccess,
      onCancel: () => setStep('photos'),
      initialFiles: selectedPhotos.map((photo) => photo.file),
      initialPreviewEntries: selectedPhotos.map((photo) => ({
        id: photo.id,
        file: photo.file,
        previewUrl: photo.previewUrl,
        isExternalPreview: true,
      })),
      pendingUploads: selectedPhotos,
      onRemovePendingUpload: removePhoto,
    };

    switch (selectedType) {
      case 'punch_item':
        return <PunchItemHotspotForm {...formProps} />;
      case 'quick_punch_item':
        return (
          <QuickPunchItemHotspotForm
            photoId={photoId}
            projectId={numericProjectId}
            viewPosition={viewPosition}
            hotspotId={hotspotId}
            pendingUploads={selectedPhotos}
            onRequestPhoto={() => quickPunchPhotoInputRef.current?.click()}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setStep('type');
              setSelectedType(null);
              clearSelectedPhotos();
            }}
          />
        );
      case 'observation':
        return <ObservationHotspotForm {...formProps} />;
      case 'rfi':
        return <RfiHotspotForm {...formProps} />;
      case 'safety':
        if (!safetyAccess.isEnabled) return null;
        return (
          <SafetyHotspotForm
            photoId={photoId}
            projectId={numericProjectId}
            viewPosition={viewPosition}
            hotspotId={hotspotId}
            onSuccess={handleFormSuccess}
            onCancel={() => setStep('photos')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      modal={false}
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent
        className={
          step === 'form'
            ? selectedType === 'quick_punch_item'
              ? 'max-h-[92vh] max-w-6xl overflow-y-auto p-4 sm:p-6 lg:p-7'
              : 'max-h-[90vh] max-w-2xl overflow-y-auto'
            : 'max-w-md'
        }
        data-tour-id="iv-panorama-fab-dialog"
      >
        <DialogHeader>
          {selectedType && step !== 'type' ? (
            <>
              <div className="flex items-center gap-2">
                {!(step === 'form' && selectedType === 'quick_punch_item') && (
                  <button
                    type="button"
                    onClick={() => {
                      if (step === 'form') {
                        if (selectedType === 'quick_punch_item') {
                          setStep('type');
                          setSelectedType(null);
                          clearSelectedPhotos();
                        } else {
                          setStep('photos');
                        }
                      } else {
                        setStep('type');
                        setSelectedType(null);
                        clearSelectedPhotos();
                      }
                    }}
                    className="p-1 -ml-1 rounded-md hover:bg-accent transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                <DialogTitle>
                  {step === 'photos' ? 'Add Photos' : ITEM_TITLES[selectedType]}
                </DialogTitle>
              </div>
              {(step === 'photos' || ITEM_DESCRIPTIONS[selectedType]) && (
                <DialogDescription>
                  {step === 'photos'
                    ? 'Take or upload photos before filling in item details. You can also skip this step.'
                    : ITEM_DESCRIPTIONS[selectedType]}
                </DialogDescription>
              )}
            </>
          ) : (
            <>
              <DialogTitle>Pin Item to Location</DialogTitle>
              <DialogDescription>
                Attach a construction item to this spot in the 360° view for spatial context.
              </DialogDescription>
            </>
          )}
        </DialogHeader>
        <input
          ref={quickPunchPhotoInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          capture="environment"
          className="hidden"
          aria-label="Take a photo for quick punch item"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []).slice(0, 1);
            event.currentTarget.value = '';
            if (files.length === 0) return;
            appendPhotos(files, 'quick_punch_item');
            setStep('form');
          }}
        />
        {step === 'type' && renderTypeSelection()}
        {step === 'photos' && selectedType && renderPhotoSelection()}
        {step === 'form' && selectedType && renderForm()}
      </DialogContent>
    </Dialog>
  );
};

export default FABActionDialog;
